import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, GraduationCap, UserCheck, UserPlus, Wallet, CalendarClock, History, Settings,
  Search, Send, Video, CalendarPlus, UserCog, LogOut, Menu, X,
} from "lucide-react";
import { Logo } from "@/components/Brand";
import { Avatar } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

const NAV = {
  admin: [
    ["/admin", "Dashboard", LayoutDashboard], ["/admin/tutors", "Tutor Management", GraduationCap],
    ["/admin/tutor-approvals", "Tutor Approvals", UserCheck], ["/admin/students", "Student Management", Users],
    ["/admin/student-approvals", "Student Approvals", UserPlus], ["/admin/credits", "Credit Management", Wallet],
    ["/admin/meetings", "Scheduled Meetings", CalendarClock], ["/admin/history", "Session History", History],
    ["/admin/settings", "Settings", Settings],
  ],
  student: [
    ["/student", "Dashboard", LayoutDashboard], ["/student/find-tutor", "Find Tutor", Search],
    ["/student/requests", "My Requests", Send], ["/student/sessions", "My Sessions", Video],
    ["/student/wallet", "Credit Wallet", Wallet], ["/student/profile", "Profile Settings", UserCog],
  ],
  tutor: [
    ["/tutor", "Dashboard", LayoutDashboard], ["/tutor/requests", "Student Requests", Send],
    ["/tutor/students", "Approved Students", Users], ["/tutor/schedule", "Schedule Session", CalendarPlus],
    ["/tutor/upcoming", "Upcoming Sessions", CalendarClock], ["/tutor/history", "Session History", History],
    ["/tutor/profile", "Profile Settings", UserCog],
  ],
};

function Sidebar({ role, onNavigate }) {
  return (
    <div className="flex h-full flex-col bg-[#0B1A33] text-slate-300 grid-texture">
      <div className="px-6 py-5"><Logo className="h-12" to={`/${role}`} testid="sidebar-logo-link" /></div>
      <p className="px-6 pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{role} portal</p>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3">
        {NAV[role].map(([to, label, Icon]) => (
          <NavLink key={to} to={to} end={to === `/${role}`} onClick={onNavigate}
            data-testid={`sidebar-${label.toLowerCase().replace(/ /g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#1F6FFF] text-white shadow-lg shadow-[#1F6FFF]/30" : "hover:bg-white/5 hover:text-white"}`}>
            <Icon className="h-4 w-4" />{label}
          </NavLink>
        ))}
      </nav>
      <div className="m-3 rounded-xl border border-white/10 bg-white/5 p-3 text-xs text-slate-400">
        <span className="font-semibold text-[#3CC34A]">100 credits</span> per completed session per student.
      </div>
    </div>
  );
}

export default function DashboardLayout({ role }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const doLogout = () => { logout(); nav(role === "admin" ? "/admin/login" : `/login/${role}`); };
  return (
    <div className="flex min-h-screen bg-[#F4F7FA]">
      <aside className="fixed inset-y-0 left-0 hidden w-64 lg:block"><Sidebar role={role} /></aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64"><Sidebar role={role} onNavigate={() => setOpen(false)} /></div>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-slate-200/80 bg-white/80 px-4 backdrop-blur-xl sm:px-8">
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="sidebar-mobile-toggle">{open ? <X /> : <Menu />}</button>
          <div className="hidden text-sm text-slate-500 lg:block">Explore to Excel™</div>
          <div className="flex items-center gap-3">
            {role === "student" && user?.wallet && (
              <span className="hidden rounded-full bg-[#3CC34A]/12 px-3 py-1 text-xs font-bold text-[#1f8f2b] sm:inline" data-testid="topbar-credits">
                {user.wallet.available} credits
              </span>
            )}
            <Avatar src={user?.profile_picture} name={user?.name} size="h-9 w-9" />
            <div className="hidden text-right sm:block">
              <p className="text-sm font-semibold text-[#0B1A33]" data-testid="topbar-user-name">{user?.name}</p>
              <p className="text-xs capitalize text-slate-500">{role}</p>
            </div>
            <button onClick={doLogout} data-testid="logout-button" className="ml-2 rounded-full p-2 text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600" title="Log out">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-8"><Outlet /></main>
      </div>
    </div>
  );
}
